/**
 * Virtualizovaná tabulka s CSV exportem (spec 08 §2, §6).
 *
 * Výkonnostní požadavek: 5k+ řádků plynule. Proto se renderují jen viditelné
 * řádky (@tanstack/react-virtual) a hlavička je sticky mimo virtualizované okno.
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type Row,
  type ExpandedState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { downloadCsv, toCsv, type CsvColumn } from '@/lib/csv';
import { EmptyState } from './EmptyState';

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  /** sloupce pro CSV — jiné než zobrazené (CSV chce syrová čísla) */
  csvColumns?: CsvColumn<T>[];
  csvFilename?: string;
  /** hierarchie web → pozice → dodavatel → fáze */
  getSubRows?: (row: T) => T[] | undefined;
  onRowClick?: (row: T) => void;
  rowHeight?: number;
  /** výška oblasti s řádky */
  height?: number;
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  initialSorting?: SortingState;
  toolbar?: ReactNode;
  getRowId?: (row: T, index: number) => string;
}

export function DataTable<T>({
  data,
  columns,
  csvColumns,
  csvFilename = 'export.csv',
  getSubRows,
  onRowClick,
  rowHeight = 38,
  height = 620,
  emptyTitle = 'Žádná data',
  emptyDescription,
  initialSorting = [],
  toolbar,
  getRowId,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const scrollRef = useRef<HTMLDivElement>(null);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, expanded },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getSubRows,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const rows = table.getRowModel().rows;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? (virtualRows[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0) : 0;

  const handleExport = useMemo(
    () => () => {
      if (!csvColumns) return;
      // Exportuje se to, co uživatel vidí — včetně řazení a rozbalených úrovní.
      const exportRows = rows.map((r) => r.original);
      downloadCsv(csvFilename, toCsv(exportRows, csvColumns));
    },
    [csvColumns, csvFilename, rows],
  );

  return (
    <div className="data-table">
      <div className="data-table__toolbar">
        <div className="data-table__count">
          {rows.length.toLocaleString('cs-CZ')} {rows.length === 1 ? 'řádek' : 'řádků'}
        </div>
        <div className="data-table__toolbar-right">
          {toolbar}
          {csvColumns ? (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={handleExport}
              disabled={rows.length === 0}
            >
              Export CSV
            </button>
          ) : null}
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} compact />
      ) : (
        <div className="data-table__scroll" ref={scrollRef} style={{ height }}>
          <table className="data-table__table">
            <thead className="data-table__head">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();
                    return (
                      <th
                        key={header.id}
                        style={{ width: header.getSize() ? `${header.getSize()}px` : undefined }}
                        className={canSort ? 'is-sortable' : undefined}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        aria-sort={
                          sorted === 'asc'
                            ? 'ascending'
                            : sorted === 'desc'
                              ? 'descending'
                              : undefined
                        }
                      >
                        <span className="data-table__th-inner">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted ? (
                            <span className="data-table__sort" aria-hidden="true">
                              {sorted === 'asc' ? '▲' : '▼'}
                            </span>
                          ) : null}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {paddingTop > 0 ? (
                <tr aria-hidden="true">
                  <td style={{ height: paddingTop }} colSpan={columns.length} />
                </tr>
              ) : null}

              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index] as Row<T> | undefined;
                if (!row) return null;
                return (
                  <tr
                    key={row.id}
                    className={`data-table__row${onRowClick ? ' is-clickable' : ''}`}
                    style={{ height: rowHeight }}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={{ paddingLeft: cell.column.id === 'name' ? `${row.depth * 16 + 10}px` : undefined }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })}

              {paddingBottom > 0 ? (
                <tr aria-hidden="true">
                  <td style={{ height: paddingBottom }} colSpan={columns.length} />
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Tlačítko pro rozbalení hierarchie — do prvního sloupce. */
export function ExpandToggle<T>({ row }: { row: Row<T> }) {
  if (!row.getCanExpand()) return <span className="expand-toggle expand-toggle--leaf" />;
  return (
    <button
      type="button"
      className="expand-toggle"
      onClick={(e) => {
        e.stopPropagation();
        row.toggleExpanded();
      }}
      aria-label={row.getIsExpanded() ? 'Sbalit' : 'Rozbalit'}
    >
      {row.getIsExpanded() ? '▾' : '▸'}
    </button>
  );
}
